/**
 * lib/cities/index.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Unified city resolver for the Experiences feature.
 *
 * GENERATED from city_catalog.ts — no manual entries needed.
 * To add a city: edit city_catalog.ts and add it there.
 *
 * Public surface:
 *   resolveCity(input, iata?)   → ExperienceCity | null
 *   findCityByAlias(input)      → ExperienceCity | null
 *   findCityByIata(iata)        → ExperienceCity | null
 *   EXPERIENCES_CITIES          → full registry keyed by IATA
 *   CITY_ALIAS_MAP              → alias → IATA (for NLP scanning in queryParser)
 *
 * Used by:
 *   lib/experiences/queryParser.ts  — NLP city matching
 *   routes/experiences.tsx          — direct IATA look-ups
 */

import { CITY_CATALOG, CATALOG_ALIAS_MAP } from "./city_catalog";

// ─── City type ────────────────────────────────────────────────────────────────

export interface ExperienceCity {
  /** IATA airport code or RHEA city code, e.g. "TYO" */
  iata:    string;
  /** Canonical English name, used in API requests */
  name:    string;
  /** Localized display name shown in the UI */
  display: string;
  /** Country name (Spanish) */
  country: string;
}

// ─── Canonical Experiences city registry ─────────────────────────────────────
// Auto-generated from CITY_CATALOG.
// ~110 cities supported — add to city_catalog.ts to expand.

export const EXPERIENCES_CITIES: Readonly<Record<string, ExperienceCity>> =
  Object.fromEntries(
    CITY_CATALOG.map(c => [
      c.iata,
      {
        iata:    c.iata,
        name:    c.name,
        display: c.display,
        country: c.country,
      } satisfies ExperienceCity,
    ])
  );

// ─── Alias → IATA map ─────────────────────────────────────────────────────────
// Auto-generated from city_catalog.ts aliases.
// Keys are lower-cased; matching is always done with .toLowerCase().trim().
// Exported so queryParser.ts can scan over alias keys without duplicating data.

export const CITY_ALIAS_MAP: Readonly<Record<string, string>> = CATALOG_ALIAS_MAP;

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Look up a city by name or alias (case-insensitive).
 * Returns the ExperienceCity or null if unrecognized.
 */
export function findCityByAlias(input: string): ExperienceCity | null {
  const iata = CITY_ALIAS_MAP[input.toLowerCase().trim()];
  return iata ? (EXPERIENCES_CITIES[iata] ?? null) : null;
}

/**
 * Look up a city by IATA code (case-insensitive).
 * Returns the ExperienceCity or null if not in the supported registry.
 */
export function findCityByIata(iata: string): ExperienceCity | null {
  return EXPERIENCES_CITIES[iata.toUpperCase().trim()] ?? null;
}

/**
 * Resolve any city input to an ExperienceCity.
 *
 * Resolution order:
 *   1. `iata` parameter — if provided, looks up EXPERIENCES_CITIES directly
 *   2. Name/alias lookup via CITY_ALIAS_MAP
 *   3. Returns null if unrecognized
 */
export function resolveCity(input: string, iata?: string): ExperienceCity | null {
  if (iata) return findCityByIata(iata);
  return findCityByAlias(input);
}
